import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useFormContext } from "react-hook-form";
import { getVariable } from "../../utils/functions";
import ReCAPTCHA from "react-google-recaptcha";

interface CardReCaptchaProps {
  name: string;
  onChange?(token: string | null): void;
}

export type CardReCaptchaRef = {
  clear: () => void;
};

const CardReCaptcha = forwardRef<CardReCaptchaRef, CardReCaptchaProps>(
  (props, ref) => {
    const reCaptchaRef = useRef<any>();

    const fetchClear = async () => {
      reCaptchaRef.current?.reset();
      reCaptchaRef.current?.handleChange(null);
    };

    useImperativeHandle(ref, () => ({
      clear: () => {
        fetchClear();
      },
    }));

    const form = useFormContext();

    if (form) {
      return ReCaptchaForm(props, reCaptchaRef);
    }

    return ReCaptchaComp(props, reCaptchaRef);
  }
);

function ReCaptchaForm(props: CardReCaptchaProps, reCaptchaRef: any) {
  const _backgroundColor = "rgb(249, 250, 251, 0.95)";

  const { name, onChange } = props;

  const [reCaptchaToken, setReCaptchaToken] = useState<string | null>(null);
  const [erro, setErro] = useState<boolean>(false);

  const inputData = useRef<any>();

  const {
    register,
    resetField,
    formState: { errors },
  } = useFormContext();

  const { ref, ...rest } = register(name);

  useEffect(() => {
    if (!inputData.current?.value) {
      resetField(name, { defaultValue: "" });
      setReCaptchaToken(null);
    }
  }, [inputData.current?.value]);

  useEffect(() => {
    if (errors[name] && !reCaptchaToken) {
      setErro(true);
    } else {
      setErro(false);
    }
  }, [errors, name, reCaptchaToken]);

  const onChangeReCaptcha = (token: string | null) => {
    setReCaptchaToken(token);
    setErro(token ? false : errors[name] && !token ? true : false);

    inputData.current.value = token;
    inputData.current.focus();

    if (onChange) {
      onChange(token);
    }
  };

  return (
    <div>
      <div className="flex flex-col">
        <div>
          <ReCAPTCHA
            ref={reCaptchaRef}
            sitekey={getVariable("REACT_APP_RECAPTCHA_KEY") as string}
            onChange={onChangeReCaptcha}
            onExpired={() => onChangeReCaptcha(null)}
          />
        </div>
        <div
          className="py-1 pl-3 text-xs text-red-600"
          style={{ backgroundColor: _backgroundColor }}
          hidden={!erro}
        >
          {errors[name]?.message?.toString()}
        </div>
      </div>

      <div style={{ height: "0px" }}>
        <input
          style={{ width: "0px" }}
          type="text"
          {...rest}
          name={name}
          ref={(e) => {
            ref(e);
            inputData.current = e;
          }}
        ></input>
      </div>
    </div>
  );
}

function ReCaptchaComp(props: CardReCaptchaProps, reCaptchaRef: any) {
  const { onChange } = props;

  const onChangeReCaptcha = (token: string | null) => {
    if (onChange) {
      onChange(token);
    }
  };

  return (
    <div className="flex flex-col">
      <ReCAPTCHA
        ref={reCaptchaRef}
        sitekey={getVariable("REACT_APP_RECAPTCHA_KEY") as string}
        onChange={onChangeReCaptcha}
        onExpired={() => onChangeReCaptcha(null)}
      />
    </div>
  );
}

export default CardReCaptcha;
